"use client";

import { useMemo } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useT } from "@/lib/i18n/context";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { WRealizationsTestimonialsContent } from "@/lib/api/cms-window-content";
import { wRealizationsTestimonialsTranslations } from "./WRealizationsTestimonials.i18n";
import styles from "./WRealizationsTestimonials.module.css";

/**
 * WRealizationsTestimonials - okno "Realizacje + testimoniale".
 *
 * Struktura:
 *   - Header centrowany: eyebrow (hero_text) + H2 (title lub defaultTitle)
 *   - Grid 3-kartowy realizacji (kazda: obrazek/logo + label pill + tytul + sector + "Zobacz realizację →")
 *   - Opcjonalny "Wszystkie realizacje" pill button (href z config[show_all_link])
 *   - Separator
 *   - Testimonial carousel (Swiper single-slide) z duzym cytatem + tekstem + autorem + dots
 */
export function WRealizationsTestimonials({ content, title }: WRealizationsTestimonialsProps) {
  const t = useT(wRealizationsTestimonialsTranslations);

  const realizations = useMemo(() => content?.realizations ?? [], [content]);
  const testimonials = useMemo(() => content?.testimonials ?? [], [content]);

  if (!content) return null;

  const heading = title || t.defaultTitle;
  const eyebrow = (content.hero_text ?? "").trim();
  const allHref = content.href ? extractInternalHref(content.href) : null;
  const hasRealizations = realizations.length > 0;
  const hasTestimonials = testimonials.length > 0;

  if (!hasRealizations && !hasTestimonials) return null;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
      </div>

      {hasRealizations ? (
        <div className={styles.grid}>
          {realizations.map((item) => {
            const href = extractInternalHref(item.href);
            return (
              <Link key={item.id} href={href ?? item.href} className={styles.card}>
                <div className={styles.cardMedia}>
                  {item.picture ? (
                    <img src={item.picture} alt={item.name} loading="lazy" className={styles.cardImg} />
                  ) : (
                    <span className={styles.cardImgPlaceholder} aria-hidden="true" />
                  )}
                  {item.label ? <span className={styles.cardLabel}>{item.label}</span> : null}
                  {item.logo ? (
                    <span className={styles.cardLogo}>
                      <img src={item.logo} alt="" loading="lazy" />
                    </span>
                  ) : null}
                </div>
                <div className={styles.cardBody}>
                  <h3 className={styles.cardTitle}>{item.name}</h3>
                  {item.short_text ? <p className={styles.cardSector}>{item.short_text}</p> : null}
                  <span className={styles.cardCta}>
                    {t.seeRealization} <span aria-hidden="true">→</span>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      ) : null}

      {allHref ? (
        <div className={styles.allWrap}>
          <Link href={allHref} className={styles.allBtn}>
            {t.seeAll} <span aria-hidden="true">→</span>
          </Link>
        </div>
      ) : null}

      {hasTestimonials ? (
        <div className={styles.testimonialsBlock}>
          <Swiper
            modules={[Autoplay, Pagination, A11y]}
            slidesPerView={1}
            loop={testimonials.length > 1}
            autoplay={testimonials.length > 1 ? { delay: 7000, disableOnInteraction: false } : false}
            pagination={testimonials.length > 1 ? { clickable: true, el: `.${styles.pagination}` } : false}
            a11y={{ enabled: true, prevSlideMessage: t.prev, nextSlideMessage: t.next, paginationBulletMessage: t.goToSlide }}
            spaceBetween={0}
            className={styles.swiper}
          >
            {testimonials.map((item) => (
              <SwiperSlide key={item.id} className={styles.slide}>
                <div
                  className={styles.testimonial}
                  dangerouslySetInnerHTML={{ __html: item.testimonial_html }}
                />
                <p className={styles.author}>
                  <span className={styles.authorSep} aria-hidden="true" />
                  <span className={styles.authorName}>
                    {item.client_name}
                    {item.client_sector ? "," : null}
                  </span>
                  {item.client_sector ? (
                    <span className={styles.authorSector}>{item.client_sector}</span>
                  ) : null}
                  <span className={styles.authorSep} aria-hidden="true" />
                </p>
              </SwiperSlide>
            ))}
          </Swiper>
          {testimonials.length > 1 ? <div className={styles.pagination} /> : null}
        </div>
      ) : null}
    </section>
  );
}

export type WRealizationsTestimonialsProps = {
  content: WRealizationsTestimonialsContent | null;
  title: string;
};

export default WRealizationsTestimonials;
